import { useState } from 'react';
import {
  MessageSquare,
  Star,
  ThumbsUp,
  ThumbsDown,
  Filter,
  Calendar,
  User,
  Package,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type FeedbackType = 'all' | 'positive' | 'negative' | 'neutral';
type FeedbackCategory = 'quality' | 'delivery' | 'communication' | 'pricing' | 'service';

interface Feedback {
  id: string;
  orderId: string;
  buyerName: string;
  rating: number;
  type: 'positive' | 'negative' | 'neutral';
  category: FeedbackCategory;
  comment: string;
  date: string;
  responded: boolean;
}

const mockFeedback: Feedback[] = [
  {
    id: 'fb-001',
    orderId: 'PO-2026-0145',
    buyerName: 'MS Celebration - GalleyOS',
    rating: 5,
    type: 'positive',
    category: 'quality',
    comment: 'Excellent quality produce. The tomatoes were perfectly ripe and the lettuce was crisp and fresh. Will definitely order again.',
    date: '2026-01-28T14:30:00Z',
    responded: true,
  },
  {
    id: 'fb-002',
    orderId: 'PO-2026-0142',
    buyerName: 'MS Paradise - Main Kitchen',
    rating: 4,
    type: 'positive',
    category: 'delivery',
    comment: 'Delivery was on time and well-packaged. Minor issue with one box being slightly damaged but contents were fine.',
    date: '2026-01-26T10:15:00Z',
    responded: true,
  },
  {
    id: 'fb-003',
    orderId: 'PO-2026-0138',
    buyerName: 'MS Horizon - Buffet',
    rating: 3,
    type: 'neutral',
    category: 'communication',
    comment: 'Order was correct but had difficulty reaching support when I had questions about substitutions. Response time could be improved.',
    date: '2026-01-24T16:45:00Z',
    responded: false,
  },
  {
    id: 'fb-004',
    orderId: 'PO-2026-0135',
    buyerName: 'MS Celebration - Bar',
    rating: 2,
    type: 'negative',
    category: 'quality',
    comment: 'Some of the citrus fruits were overripe. Had to discard about 10% of the order. Please improve quality control.',
    date: '2026-01-22T09:00:00Z',
    responded: true,
  },
  {
    id: 'fb-005',
    orderId: 'PO-2026-0130',
    buyerName: 'MS Paradise - Specialty Restaurant',
    rating: 5,
    type: 'positive',
    category: 'service',
    comment: 'Outstanding service! The team went above and beyond to accommodate our last-minute order change. Highly recommended.',
    date: '2026-01-20T11:30:00Z',
    responded: true,
  },
  {
    id: 'fb-006',
    orderId: 'PO-2026-0125',
    buyerName: 'MS Horizon - Room Service',
    rating: 4,
    type: 'positive',
    category: 'pricing',
    comment: 'Competitive pricing and good value for the quality provided. Appreciate the volume discounts.',
    date: '2026-01-18T13:20:00Z',
    responded: false,
  },
];

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  quality: 'Product Quality',
  delivery: 'Delivery',
  communication: 'Communication',
  pricing: 'Pricing',
  service: 'Customer Service',
};

const CATEGORY_COLORS: Record<FeedbackCategory, string> = {
  quality: 'bg-purple-100 text-purple-800',
  delivery: 'bg-blue-100 text-blue-800',
  communication: 'bg-amber-100 text-amber-800',
  pricing: 'bg-green-100 text-green-800',
  service: 'bg-pink-100 text-pink-800',
};

function StarRating({ rating }: { readonly rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

function getFeedbackIcon(type: Feedback['type']) {
  switch (type) {
    case 'positive':
      return ThumbsUp;
    case 'negative':
      return ThumbsDown;
    default:
      return MessageSquare;
  }
}

function getFeedbackColor(type: Feedback['type']): string {
  switch (type) {
    case 'positive':
      return 'text-green-600 bg-green-100';
    case 'negative':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function FeedbackPage() {
  const [filter, setFilter] = useState<FeedbackType>('all');

  const filteredFeedback = filter === 'all'
    ? mockFeedback
    : mockFeedback.filter((f) => f.type === filter);

  // Calculate stats
  const stats = {
    total: mockFeedback.length,
    positive: mockFeedback.filter((f) => f.type === 'positive').length,
    negative: mockFeedback.filter((f) => f.type === 'negative').length,
    neutral: mockFeedback.filter((f) => f.type === 'neutral').length,
    avgRating: mockFeedback.reduce((sum, f) => sum + f.rating, 0) / mockFeedback.length,
    pendingResponse: mockFeedback.filter((f) => !f.responded).length,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Customer Feedback</h1>
        <p className="text-muted-foreground">
          View and respond to feedback from your customers
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-amber-100 p-3">
              <Star className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Rating</p>
              <p className="text-xl font-bold">{stats.avgRating.toFixed(1)}/5</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-green-100 p-3">
              <ThumbsUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Positive</p>
              <p className="text-xl font-bold text-green-600">{stats.positive}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-red-100 p-3">
              <ThumbsDown className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Negative</p>
              <p className="text-xl font-bold text-red-600">{stats.negative}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-full bg-blue-100 p-3">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Response</p>
              <p className="text-xl font-bold">{stats.pendingResponse}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </Button>
        <Button
          variant={filter === 'positive' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('positive')}
          className={filter === 'positive' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          <ThumbsUp className="h-4 w-4 mr-1" />
          Positive ({stats.positive})
        </Button>
        <Button
          variant={filter === 'neutral' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('neutral')}
        >
          <MessageSquare className="h-4 w-4 mr-1" />
          Neutral ({stats.neutral})
        </Button>
        <Button
          variant={filter === 'negative' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('negative')}
          className={filter === 'negative' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          <ThumbsDown className="h-4 w-4 mr-1" />
          Negative ({stats.negative})
        </Button>
      </div>

      {/* Feedback List */}
      <FeedbackContent feedback={filteredFeedback} filter={filter} />
    </div>
  );
}

interface FeedbackContentProps {
  readonly feedback: Feedback[];
  readonly filter: FeedbackType;
}

function FeedbackContent({
  feedback, filter 
}: FeedbackContentProps) {
  if (feedback.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium">No feedback found</p>
          <p className="text-muted-foreground">
            {filter === 'all'
              ? 'You have not received any feedback yet'
              : `No ${filter} feedback to display`}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {feedback.map((item) => {
        const FeedbackIcon = getFeedbackIcon(item.type);
        const feedbackColor = getFeedbackColor(item.type);

        return (
          <Card key={item.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className={`rounded-full p-2 ${feedbackColor}`}>
                  <FeedbackIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StarRating rating={item.rating} />
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[item.category]}`}>
                          {CATEGORY_LABELS[item.category]}
                        </span>
                        {item.responded ? (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Responded
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="h-3 w-3" />
                            Needs Response
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.buyerName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {item.orderId}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(item.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">{item.comment}</p>
                  {!item.responded && (
                    <div className="mt-3">
                      <Button size="sm" variant="outline">
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Respond
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
